import { ChangeDetectorRef, Component } from '@angular/core';
import { State } from '@progress/kendo-data-query';
import {
  GridState,
  GridStateCompositeFilterDescriptor,
  MessageService,
  NamedEntity,
} from '@shared';
import {
  CategoryService,
  GlobalEventsService,
  ItemService,
  RestaurantService,
  SelectableTier,
  SelectableTieredRestaurant,
} from '@shared-data';
import {
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  Subject,
  tap,
} from 'rxjs';
import {
  allProductType,
  AvailabilityAccessByProductType,
  availabilityProductTypes,
  ExportOption,
} from '../../constants';
import { ActivatedRoute, Router } from '@angular/router';
import { BaseAvailabilityChangesetComponent } from '../base-availability-changeset/base-availability-changeset.component';
import { loadOuterSelection, saveOuterSelection } from '../top-level-filter-cache';
import { CheckBoxState } from '@progress/kendo-angular-inputs';
import { TokenService } from '@auth';

@Component({
  selector: 'app-availability',
  templateUrl: './availability.component.html',
  styleUrl: './availability.component.scss',
})
export class AvailabilityComponent {
  tieredRestaurants: SelectableTieredRestaurant[] = [];

  tiers: SelectableTier[] = [];

  showingDataFor?: SelectableTieredRestaurant;

  _productType!: NamedEntity;

  get productType() {
    return this._productType;
  }

  set productType(v: NamedEntity) {
    this._productType = v;
    if (v && this.selectedRestaurants.size > 0) {
      this.fetchFilters();
    }
  }

  get hasWriteAccess() {
    if (!AvailabilityAccessByProductType.has(this.productType.id)) return false;
    return AvailabilityAccessByProductType.get(this.productType.id) == 'write';
  }

  restaurantSearch: any = new Subject<string>();

  restaurantId?: string;

  locked: boolean = false;

  productTypes = availabilityProductTypes;

  categories: NamedEntity[] = [];

  defaultCategory = new NamedEntity('-1', 'Category (All)');
  selectedCategory: NamedEntity = this.defaultCategory;

  machineTypes: NamedEntity[] = [];
  defaultMachineType = new NamedEntity('-1', 'Machine (All)');
  selectedMachineType: NamedEntity = this.defaultMachineType;

  component?: BaseAvailabilityChangesetComponent;

  isAvailabilityOnly = false;

  restaurantSearchText = '';

  // AvailabilityOnly users only get the restaurants assigned to them in RSIWeb.
  // An empty list without a search term means they have none assigned.
  get hasNoAssignedRestaurants(): boolean {
    return (
      this.isAvailabilityOnly &&
      this.tieredRestaurants.length === 0 &&
      !this.restaurantSearchText
    );
  }

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private restaurantService: RestaurantService,
    private messageService: MessageService,
    private categoryService: CategoryService,
    private cdr: ChangeDetectorRef,
    private globalEventsService: GlobalEventsService,
    private itemService: ItemService,
    private tokenService: TokenService,
  ) {
    const urlType = this.activatedRoute.snapshot.firstChild?.url[0]?.path;
    const cached = loadOuterSelection('availability');
    this.productType =
      this.productTypes.find(
        (t) => t.id === (urlType ?? cached?.productTypeId),
      ) ?? allProductType;

    if (cached?.category && cached.productTypeId === this.productType.id) {
      this.cachedCategory = cached.category;
    }
  }

  private cachedCategory?: { id: string; name: string };

  async ngOnInit() {
    this.isAvailabilityOnly = await this.tokenService.isAvailabilityOnly();

    this.subscribeToRestaurantSearch();

    this.subscribeToPendingChangesChange();
    
    this.selectedRestaurants = new Set<string>(
      JSON.parse(sessionStorage.getItem('selectedRestaurants') ?? '[]'),
    );

    this.fetchTieredRestaurants({});
  }

  updatePendingChanges() {
    this.globalEventsService.updatePendingChanges();
  }

  subscribeToPendingChangesChange() {
    this.globalEventsService.pendingChangesUpdated$.subscribe(() => {
      this.updateParameters();
    });
  }

  fetchTieredRestaurants(state: State) {
    this.restaurantService.getTieredRestaurants(state).subscribe({
      next: (restaurants) => {
        const newRestaurants = restaurants.data.map(
          (restaurant) =>
          {
            const newRestaurant = new SelectableTieredRestaurant(
              restaurant,
              restaurant.priceTierId,
              restaurant.priceTierName,
              restaurant.isKing,

            );

            newRestaurant.selected = this.selectedRestaurants.has(restaurant.id);

            return newRestaurant;
          }
            
        );

        this.tieredRestaurants = newRestaurants;

        this.refreshTierPrice();
      },
      error: (err) => {
        this.messageService.error(err);
      },
    });
  }

  refreshTierPrice() {
    this.showingDataFor = this.tieredRestaurants.find(
      (restaurant) =>
        (restaurant.isKing && restaurant.selected) || restaurant.selected,
    );
    this.locked =
      this.tiers.find((tier) => tier.selected && tier.isLocked) !== undefined;

    if ((this.restaurantId == null || this.restaurantId == undefined) 
      && this.showingDataFor?.id != null) {
      this.restaurantId = this.showingDataFor?.id;
      this.fetchFilters();
    }
    else {
      this.restaurantId = this.showingDataFor?.id;
      this.updateParameters();
    }
  }

  selectedRestaurants: Set<string> = new Set<string>();

  handleFilter(enteredValue: any) {
    this.restaurantSearch.next(enteredValue);
  }

  subscribeToRestaurantSearch() {
    this.restaurantSearch
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap((searchText) => {
          this.restaurantSearchText = searchText ? String(searchText) : '';

          const state = {
            filter: new GridStateCompositeFilterDescriptor()
              .or({
                field: 'dmaName',
                operator: 'contains',
                value: searchText,
              })
              .or({
                field: 'cityName',
                operator: 'contains',
                value: searchText,
              })
              .or({
                field: 'name',
                operator: 'contains',
                value: searchText,
              })
              .or({
                field: 'priceTierName',
                operator: 'contains',
                value: searchText,
              }),
          };

          this.fetchTieredRestaurants(state);
        }),
      )
      .subscribe();
  }

  updateParameters() {
    saveOuterSelection('availability', {
      productTypeId: this.productType.id,
      category:
        this.selectedCategory.id !== '-1'
          ? { id: this.selectedCategory.id, name: this.selectedCategory.name }
          : null,
    });

    if (this.selectedRestaurants.size > 0) {
      const queryParams = {
        restaurantId: this.restaurantId ?? null,
        restaurantIds: Array.from(this.selectedRestaurants).join(','),
        category:
          this.selectedCategory.id !== '-1' ? this.selectedCategory.id : null,
        machine:
          this.selectedMachineType.id !== '-1'
            ? this.selectedMachineType.id
            : null,
        _refresh: Date.now(),
      };
      this.router.navigate([this.productType.id], {
        relativeTo: this.activatedRoute,
        queryParams,
      });
    } else {
      this.component?.cache.clear();
    }
  }

  fetchFilters() {
    combineLatest([
      this.categoryService.getCategories(
        this.restaurantId ?? '',
        this.productType.id,
      ),
      this.itemService.getMachineDependencies({} as GridState),
    ]).subscribe({
      next: ([categoriesResponse, machineTypesResponse]) => {
        this.categories = categoriesResponse.data;
        if (this.cachedCategory) {
          this.selectedCategory =
            this.categories.find((c) => c.id === this.cachedCategory!.id) ??
            this.defaultCategory;
          this.cachedCategory = undefined;
        } else {
          this.selectedCategory = this.categories.includes(this.selectedCategory) ? this.selectedCategory : this.defaultCategory;
        }
        this.machineTypes = machineTypesResponse.data;
        this.selectedMachineType = this.machineTypes.includes(this.selectedMachineType) ? this.selectedMachineType : this.defaultMachineType;
        this.updateParameters();
      },
    });
  }

  onActivate(componentRef: any) {
    this.component = componentRef as BaseAvailabilityChangesetComponent;
    this.cdr.detectChanges();
  }

  saveChanges() {
    if (this.component) {
      this.component.saveChanges(Array.from(this.selectedRestaurants));
    }
  }

  exportToExcel(exportOption: ExportOption) {
    if (this.component) {
      this.component.exportToExcel(exportOption);
    }
  }

  updateFontSize(fontSize: number) {
    if (this.component) {
      this.component.fontSize = fontSize;
    }
  }

  hasFiltersOrData(): boolean {
    const hasFilters =
      (this.component?.state?.filter?.filters?.length ?? 0) > 0;
    const hasData = (this.component?.changeset?.total ?? 0) > 0;
    return hasFilters && hasData;
  }

  saveBulkFilteredChanges(enabled: boolean) {
    if (this.component) {
      this.component.saveBulkFilteredChanges(Array.from(this.selectedRestaurants), enabled);
    }
  }

  toggleSelectAllTieredRestaurants(event: CheckBoxState) {
    this.tieredRestaurants.forEach((restaurant) => {
      restaurant.selected = event === true;
      if (restaurant.selected) {
        this.selectedRestaurants.add(restaurant.id);
      } else {
        this.selectedRestaurants.delete(restaurant.id);
      }
    });

    sessionStorage.setItem('selectedRestaurants', JSON.stringify(Array.from(this.selectedRestaurants)));

    this.refreshTierPrice();
  }

  get allTieredRestaurantsSelected(): boolean {
    return this.tieredRestaurants.every((restaurant) => restaurant.selected);
  }

  toggleRestaurantSelect(restaurant: SelectableTieredRestaurant) {
    restaurant.toggleSelect();

    if (restaurant.selected) {
      this.selectedRestaurants.add(restaurant.id);
    } else {
      this.selectedRestaurants.delete(restaurant.id);
    }

    sessionStorage.setItem('selectedRestaurants', JSON.stringify(Array.from(this.selectedRestaurants)));

    this.refreshTierPrice();
  }
}
